/**
 * The third live route (#26 / R6.1) — where a family lands after creating a household.
 *
 * **What this fixes, and what it deliberately does not.**
 *
 * A family who signed up through the now-live `/signup` was sent here and read
 * *"Set up the **Silva** family"* — a name from the demo fixtures, belonging to nobody. This route
 * asks the server who they actually are (`GET /api/tenants/current`) and uses that name.
 *
 * Since then, in order: F-30 gave the payday choices somewhere to go (`PATCH /api/tenants/current`,
 * saved when the family lights the first lantern); slice A wired the kids recap to `GET /api/users`;
 * and slice B3 wired the PACKS step to `GET /api/quest-packs` and
 * `PUT /api/quest-packs/:key/selection`. The note above the default export says exactly what is
 * live and what is still not.
 *
 * The "Preview. Fixture data. No server." banner comes from `Surface`'s fixture mode in `App.jsx`,
 * and a live container leaves fixture mode — so going live silently REMOVES that warning. The
 * subheading carries the true sentence instead: the choices are saved on finish.
 *
 * `onFinish` RESOLVES only on a real save. `Onboarding.handleFinish` celebrates only after it
 * resolves, so a failed save never produces a false "First lantern lit" — the clause that produced
 * the defect this route first repaired. The same save is the only way off this screen to Today
 * (#639): a rejected one keeps the family here to try again.
 */
import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import LanternLoader from '../components/LanternLoader.jsx';
import SampleWeekPanel, { STALE_LIST } from '../components/SampleWeekPanel.jsx';
import { ApiError } from '../api/client.js';
import {
  conflictSelection,
  fetchQuestPacks,
  newMutationId,
  packErrorCode,
  setQuestPackSelection,
} from '../api/questPacks.js';
import { fetchTenant, updateTenant, fetchFamilyMembers } from '../api/session.js';
import {
  MEMBER_EXISTS,
  createFamilyChild,
  generateInitialPassword,
  memberConflictDetail,
  memberErrorCode,
  suggestUsername,
} from '../api/familyMembers.js';
import {
  EMPTY_CHILD_DRAFT,
  clearChildDraft,
  readChildDraft,
  writeChildDraft,
} from './childDraft.js';
import {
  EMPTY_HOST_DRAFT,
  clearHostDraft,
  readHostDraft,
  writeHostDraft,
} from './hostDraft.js';
import {
  QUEST_UNCONFIRMED,
  createFamilyQuest,
  fetchFamilyQuests,
  questErrorCode,
  updateFamilyQuestReward,
} from '../api/familyQuests.js';
import {
  EMPTY_QUEST_DRAFT,
  clearQuestDraft,
  readQuestDraft,
  writeQuestDraft,
} from './questDraft.js';
import { art } from '../assets.js';
import {
  AD_HOC, CALENDAR_MONTH, PAYDAY_PERIODS, dayNumberFromPaydayPeriod, paydayKind, paydayPeriodFromDayNumber,
} from '../lib/payday.js';
import { CONFIRMATION_REQUIRED, codeOf } from '../api/paydayChange.js';
import { WEEKEND_CHANGE_PENDING, WEEKEND_RULE_PENDING } from '../lib/weekendRule.js';
import PaydayChangePanel, { usePaydayChange } from '../components/PaydayChangePanel.jsx';
import { useOptionalActor } from '../state/ActorContext.jsx';

const Onboarding = lazy(() => import('../pages/Onboarding.jsx'));

/** Said beside the ceremony button while the payday change is not confirmed. True in every case. */
export const PAYDAY_NOT_DONE = 'Setup is not finished yet — see the payday note above.';

/** The ceremony button's label while the payday warning waits for the parent. */
export const PAYDAY_WAITING = 'Waiting for your answer about payday, above…';

/**
 * #580 4a3: the period step 3 chose, or `null` when the family chose none. A monthly or ad hoc
 * TYPE wins over a weekday, because choosing a weekday sets the type back to weekly.
 */
export function chosenPaydayPeriod({ paydayDay, paydayType } = {}) {
  if (paydayType === 'monthly') return CALENDAR_MONTH;
  if (paydayType === 'ad_hoc') return AD_HOC;
  return paydayDay === null || paydayDay === undefined ? null : paydayPeriodFromDayNumber(paydayDay);
}

/**
 * Said in the subheading. It used to warn that nothing here was saved, which was true until F-30
 * gave these three choices somewhere to go. Saying it now would be a lie in the other direction.
 */
export const SAVED_ON_FINISH =
  'Pick what suits your family — your choices are saved when you light the first lantern.';

/**
 * Where a family goes once their setup is saved (#639). Today, the screen sign-in lands on
 * (`LoginRoute.jsx`). It reads the family from the server, so nothing is handed over on the way.
 */
export const LANDING_ROUTE = '/today';

/** What a family reads when the save itself failed. It names the one thing they can do. */
export const SAVE_FAILED =
  'We could not save those choices just now — please try again in a moment.';

/* -------------------------------------------------------- AD-04, the add-a-child sentences */

/** Each one names the ONE thing the family can do next, and none of them claims a child was saved. */
export const ADD_CHILD_NEEDS_NAME = 'Please type your child’s name — at least two letters.';
export const ADD_CHILD_NEEDS_USERNAME =
  'Please give your child a sign-in name. Letters and numbers only.';
export const ADD_CHILD_NEEDS_BIRTHDAY =
  'Please add a birthday. The app needs it to pitch quests at the right age.';
export const ADD_CHILD_FAILED =
  'We could not add that child just now — nothing was saved. Please try again in a moment.';

/* --------------------------------------------------- AD-04, the add-a-quest sentences (#576 o6) */

/** Each one names the ONE thing the family can do next, and none claims a quest was saved. */
export const ADD_QUEST_NEEDS_NAME = 'Please name the quest — at least two letters.';
export const ADD_QUEST_FAILED =
  'We could not add that quest just now — nothing was saved. Please try again in a moment.';

/**
 * Said when the name is already on the account, and nothing was written.
 *
 * It is careful for the same reason the child's 409 sentence is: the browser cannot tell a REPLAY
 * of its own write from a family deliberately typing a name they already used. The sentence is true
 * of both, and it never says "added".
 */
export const ADD_QUEST_ALREADY =
  'That quest is already on your list, so nothing was added twice. Give it a different name if you meant a new one.';

/**
 * Said when the server ACCEPTED the write but returned no row this code could show.
 *
 * It claims neither outcome, because neither is known — the same care `ADD_CHILD_UNCONFIRMED`
 * takes. Saying "saved" would be a claim about a row nobody has seen; saying "failed" would be a
 * different lie, because the server did not report one. The one honest instruction is to look.
 */
export const ADD_QUEST_UNCONFIRMED =
  'We could not confirm that quest was added. Nothing was typed away — please reload this page and check the list before adding it again.';

/* ---------------------------------------------- #577 row 3, the reward review sentences */

export const QUEST_REWARD_FAILED =
  'We could not change that reward just now — nothing was saved. Please try again in a moment.';
export const QUEST_REWARD_UNCONFIRMED =
  'We could not confirm the new reward. Please reload this page and check the list before changing it again.';
export const QUEST_REWARD_SIGNED_OUT =
  'Your sign-in has expired — please sign in again. The reward was not changed.';
export const QUEST_REWARD_NEEDS_GROWNUP =
  'Changing a reward needs a grown-up to confirm it is you — press Save reward again to do that. The reward was not changed.';
export const QUEST_REWARD_NOT_A_PARENT =
  'Only a parent can change a reward, and this is a member account. The reward was not changed.';

/**
 * The sentence for a reward save that did not land — the same split the Quest Studio board uses
 * (`moveFailedMessage`). A 401 or a cancelled grown-up check is not something "try again in a
 * moment" fixes. The admin write commits before reading its receipt, so a 500, a gateway error,
 * or a dropped connection leaves the saved amount unknown.
 */
export function questRewardFailedMessage(error) {
  if (questErrorCode(error) === QUEST_UNCONFIRMED) return QUEST_REWARD_UNCONFIRMED;
  if (!(error instanceof ApiError)) return QUEST_REWARD_UNCONFIRMED;
  if (error.status === 401) return QUEST_REWARD_SIGNED_OUT;
  if (error.status === 403 && error.body && error.body.code === 'PARENT_REAUTH_REQUIRED') return QUEST_REWARD_NEEDS_GROWNUP;
  if (error.status === 403) return QUEST_REWARD_NOT_A_PARENT;
  // updateChore commits before its readback. A server error or dropped response cannot prove rollback.
  if (error.status >= 500) return QUEST_REWARD_UNCONFIRMED;
  return QUEST_REWARD_FAILED;
}

/** Said on the write the server confirmed, with the SERVER's amount. */
export function questRewardSaved(title, reward) {
  return `${title} now pays $${Number(reward).toFixed(2)}.`;
}

/** Said once, on the write that actually created the quest. */
export function addQuestSaved(title) {
  return `${title} is on your family's quest list.`;
}

/**
 * The 409 sentence, and it is deliberately careful.
 *
 * A 409 from `POST /api/admin/users` means `tenant_id` + `LOWER(username)` is already taken. The
 * browser cannot tell a REPLAY of its own write from a genuinely different child whose suggested
 * sign-in name collides, so the sentence must be true of both. What it never says is "added", and
 * what it never does is show a second chip.
 */
export const ADD_CHILD_ALREADY =
  'That sign-in name is already used in your family, so nothing was added twice. If this is a different child, pick another sign-in name.';

/**
 * The same sentence when the server offered a free name (#580). The holder is not in the list (a
 * removed account still holds the name), so it cannot be the child this attempt meant to add.
 */
export function addChildAlready(suggestion = null) {
  return suggestion
    ? `That sign-in name is already used in your family, so nothing was added twice. ${tryFreeName(suggestion)}`
    : ADD_CHILD_ALREADY;
}

/**
 * Said when the server ACCEPTED the write but the family list does not show the child, and when the
 * name is held by "some account" (#580 `SIGN_IN_NAME_TAKEN`) but the list could not be read to say
 * whose: that holder may be the child this very attempt's twin just added.
 *
 * It claims neither outcome, because neither is known. Saying "saved" would be the false progress
 * this criterion forbids; saying "failed" would be a different lie, since the server answered 2xx
 * and the row may well exist. The one honest instruction is to look.
 */
export const ADD_CHILD_UNCONFIRMED =
  'We could not confirm that child was added. Nothing was typed away — please reload this page and check the list before adding them again.';

/**
 * Said once, on the attempt that actually created the child.
 *
 * It carries the password in plain sight, and that is deliberate. A child has no email address, so
 * there is no set-your-own link to send; the server requires a password for a non-email account and
 * nobody else can ever read this one back. `src/pages/AdminUsers.js` in the production app reveals a
 * created password the same way. The sentence says plainly that it will not be shown again, and
 * where to change it, because a parent who does not write it down has to reset it.
 */
export function addChildSaved(theirName, username, password) {
  return `${theirName} is in your family. They sign in as ${username} with the password ${password} — write it down now, because this is the only time it is shown. You can change it later in Settings.`;
}

/**
 * The fix, said after the reason (#580). When the server offered a free name it is already in the
 * sign-in name box, and the family only has to press the button again — or change it.
 */
function tryFreeName(suggestion) {
  return suggestion
    ? `We put “${suggestion}” in the sign-in name box instead. Press Add to the family to use it, or change it.`
    : 'Please pick a different sign-in name for this child.';
}

/** Said when the sign-in name belongs to somebody else in the family. Naming them names the fix. */
export function addChildTakenBy(theirName, suggestion = null) {
  return `That sign-in name already belongs to ${theirName}. ${tryFreeName(suggestion)}`;
}

/**
 * Said when ANOTHER family uses the sign-in name (#580, d-20261002-10). It says why in plain words,
 * because "taken" with nobody in the list looks like a mistake. It never says which family: the
 * server does not tell us, and it must not.
 */
export function addChildTakenElsewhere(username, suggestion = null) {
  return `Another family already uses “${username}” as a sign-in name, and each sign-in name can belong to only one person. ${tryFreeName(suggestion)}`;
}

/**
 * Find the member holding a username, case-insensitively.
 *
 * Case-insensitively because the server's own check is `LOWER(username)`
 * (`server/services/admin.service.js`), so matching case-sensitively here would miss exactly the
 * row that caused the 409.
 */
export function findMemberByUsername(members, username) {
  if (!Array.isArray(members) || typeof username !== 'string') return null;
  const wanted = username.toLowerCase();
  return (
    members.find(
      (m) => m && typeof m === 'object' && typeof m.username === 'string' && m.username.toLowerCase() === wanted,
    ) || null
  );
}

/**
 * One sentence for a failed SAVE, kept apart from `tenantMessage`, which explains a failed LOAD.
 *
 * A 403 is its own answer here and nowhere else on this screen: `PATCH /api/tenants/current` is
 * guarded by `verifyAdmin`, so a family member who is not a parent gets one. Telling them to "try
 * again" would send them round a loop that cannot end.
 */
/**
 * Map `GET /api/users` rows onto the wizard's `kids` shape, `{ id, name, age, avatar }`.
 *
 * **THERE IS NO `child` ROLE IN THIS PRODUCT.** `server/services/admin.service.js` binds the
 * literal `'user'` for every admin-created account, so `role === 'user'` is the best available
 * signal and is NOT proof that a member is a child. A second adult on the account would appear
 * here. Said plainly rather than hidden, because the heading above this list says "kids".
 *
 * **THIS FAILS CLOSED, and that direction was chosen from a measurement rather than a preference.**
 * On 2026-09-12 the dev host ran this client at `dev-…-21ab8e9a` while the product API ran
 * `chore-app:pr-517` — a build from a CLOSED pull request, which excludes the server half that adds
 * `role` and `age`. `GET /api/users` there returns neither. The original filter was
 * `role !== 'admin'`, which is TRUE for `undefined`, so every member came back as a kid and **the
 * parent appeared in the kids list**. Requiring `role === 'user'` means a stale or partial API
 * yields an EMPTY recap instead of a wrong one. An empty recap is a smaller error than a wrong one,
 * and it is the error a family can see is an error.
 *
 * `age` is whatever the server computed, and a `null` stays `null` — "age unknown" and "newborn"
 * are different facts, and `Onboarding` renders the absence rather than a zero.
 *
 * A non-array yields `[]`, so a failed read costs the recap and never the wizard.
 *
 * @param {unknown} members rows from `fetchFamilyMembers`
 * @returns {Array<{id: *, name: string, age: number|null, avatar: string|undefined}>}
 */
/**
 * The role every admin-created non-admin account carries (`admin.service.js` binds this literal).
 * Named rather than inlined so the fail-closed comparison in `toKids` reads as a deliberate
 * allowlist rather than a typo.
 */
const CHILD_ROLE = 'user';

export function toKids(members) {
  if (!Array.isArray(members)) return [];
  return members
    .filter((m) => m && typeof m === 'object' && m.role === CHILD_ROLE)
    .map((m) => ({
      id: m.id,
      name: m.display_name || m.first_name || m.username || '',
      age: typeof m.age === 'number' ? m.age : null,
      avatar: m.avatar_url || undefined,
    }));
}

/**
 * The chore icons this app actually ships, under `app/public/art/` (copied there by
 * `app/scripts/copy-art.mjs`). Listed rather than guessed at, because the server names many more
 * (`dishwasher`, `vacuum`, `mop`, …) and an `<img>` for a file that is not there is a broken
 * image on a family's first screen. A name outside this set gets no `src`, which `QuestPackCard`
 * renders as its generic chore glyph — a smaller error than a broken picture, and an honest one.
 */
const SHIPPED_ICON_NAMES = new Set(['bed', 'broom', 'dishes', 'dog', 'homework', 'laundry', 'plant', 'trash']);

/**
 * `GET /api/quest-packs` sends `icons` as `{ name, alt }` — `name` is `chore_templates.icon_name`
 * (`quest-packs.service.js`, the `ic` lateral). The deck wants `{ src, alt }`. This is the one
 * place that difference is bridged.
 */
function toIcons(icons) {
  if (!Array.isArray(icons)) return [];
  return icons
    .filter((icon) => icon && typeof icon === 'object')
    .map((icon) => ({
      src: typeof icon.name === 'string' && SHIPPED_ICON_NAMES.has(icon.name) ? art(`icon-${icon.name}.png`) : null,
      alt: typeof icon.alt === 'string' ? icon.alt : '',
    }));
}

/**
 * One plain sentence for each `unavailableReason` the server sends (`retired` for an inactive
 * pack, `empty` for one with no active quests), and a safe default for a reason this client has
 * not met. Shown in the pack's subtitle beside its disabled toggle.
 */
export function unavailableSentence(reason) {
  if (reason === 'retired') return 'This pack has been retired.';
  if (reason === 'empty') return 'This pack has no quests yet.';
  return 'This pack is not available right now.';
}

/**
 * Map `GET /api/quest-packs` rows onto the deck's shape.
 *
 * What is deliberately NOT here: `valueLabel`. The fixture showed a price range per pack and the
 * component has a slot for it, but the server sends no price, and inventing one is exactly the
 * sample-data-as-live-state defect F-13 names. With the prop absent the card reads "6 quests" and
 * nothing more, which is everything the server said.
 *
 * `revision` is carried VERBATIM — a bigint string, never parsed, never coerced. A pack the server
 * sent without one keeps that absence, and `setQuestPackSelection` then refuses before the
 * network rather than inventing a `'0'`.
 *
 * A non-array yields `[]`, so a failed read costs the packs and never the wizard.
 *
 * @param {unknown} packs rows from `fetchQuestPacks`
 * @returns {Array<object>}
 */
/**
 * Map `GET /api/admin/chores` rows onto the wizard's `quests` shape (AD-04, #576 o6).
 *
 * `who` is the honest answer rather than a guess. A first quest is created with
 * `assignmentType: 'no-assignment'`, so nobody is assigned, and naming a child there would put
 * their name on work they never agreed to. Anything else is reported as the server described it.
 *
 * FAILS CLOSED on a row it cannot read: a chore with no name is dropped rather than shown as a
 * blank card, because an empty row in a "starting quests" list reads as a quest that exists.
 *
 * @param {Array} chores rows from the server
 * @returns {Array} `{ id, title, who, reward }`. Never throws.
 */
export function toQuests(chores) {
  if (!Array.isArray(chores)) return [];
  return chores
    .filter((chore) => chore && typeof chore === 'object' && typeof chore.name === 'string' && chore.name.trim() !== '')
    .map((chore) => ({
      id: chore.id,
      title: chore.name,
      who: chore.assignmentType === 'no-assignment' || chore.assignmentType == null ? 'Anyone' : undefined,
      reward: typeof chore.reward === 'number' ? chore.reward : undefined,
      // #577 L19. The server's own tag, so an edited example is still labelled and a same-name
      // quest the family wrote is never mistaken for one.
      example: chore.sampleOrigin === 'sample-week-v1',
    }));
}

export function toPacks(packs) {
  if (!Array.isArray(packs)) return [];
  return packs
    .filter((p) => p && typeof p === 'object')
    .map((p) => {
      const available = p.available !== false;
      const pack = {
        id: p.id,
        title: p.title,
        age: p.age,
        icons: toIcons(p.icons),
        questCount: p.questCount,
        added: p.added === true,
        revision: p.revision,
        available,
        unavailableReason: available ? null : (p.unavailableReason ?? null),
      };
      if (!available) {
        // The deck prefers `subtitle` over `age`, so the reason rides beside the age line.
        pack.subtitle = [p.age, unavailableSentence(p.unavailableReason)].filter(Boolean).join(' · ');
      }
      return pack;
    });
}

/** Said when a 409 reports the pack changed under the family's feet. One sentence, no retry. */
export const SELECTION_CONFLICT =
  'That change did not take — this pack was updated somewhere else, so we have shown the saved choice.';

/** Said while the ceremony button is held for a sample-week change still in flight (#577 L19). */
export const SAMPLE_CHANGE_PENDING = 'One moment — the example quests are still changing. Then light the lantern.';

/** Said when the ceremony button is pressed while a pack choice is still saving. */
export const PACK_SAVE_PENDING = 'One moment — a pack choice is still saving. Then light the lantern.';

/** Said when a 409 reports the pack cannot be added any more. */
export const PACK_UNAVAILABLE_NOW =
  'That change did not take — this pack is not available to add right now.';

/**
 * One sentence for a toggle that did not take. A 409 is worded by its `details.code`; anything
 * else reads as a save failure (`saveMessage` already knows a 403 means "ask a parent" and a 401
 * means "sign in again", and both apply to the admin-only pack endpoints).
 */
export function toggleMessage(error) {
  if (packErrorCode(error) === 'PACK_UNAVAILABLE') return PACK_UNAVAILABLE_NOW;
  if (conflictSelection(error)) return SELECTION_CONFLICT;
  return saveMessage(error);
}

/**
 * What the deck says when the pack LIST could not be read. `true` hands the deck its own default
 * sentence ("We couldn't load the quest packs just now. The rest of your setup still works."),
 * which is right for a transient fault. It is WRONG for the two refusals below, because the rest
 * of the setup does not work for them either: `requireAdmin` gates the whole pack router and
 * `verifyAdmin` gates the settings PATCH, so a non-parent would be promised a finish that then
 * refuses them (B3 review F-3).
 *
 * @param {unknown} error what `fetchQuestPacks` threw
 * @returns {string|true}
 */
export function packsMessage(error) {
  if (!(error instanceof ApiError)) return true;
  if (error.status === 403) return 'Only a parent can choose quest packs — ask them to finish setting up.';
  if (error.status === 401) {
    return 'Your sign-in has expired — please sign in again to see the quest packs.';
  }
  return true;
}

export function saveMessage(error) {
  if (!(error instanceof ApiError)) return SAVE_FAILED;
  if (error.status === 403) {
    return 'Only a parent can change the family settings — ask them to finish setting up.';
  }
  if (error.status === 401) {
    return 'Your sign-in has expired — please sign in again to save these choices.';
  }
  // #583 unit 6: a weekend change already made today; tomorrow's rule cannot be replaced.
  if (codeOf(error) === WEEKEND_RULE_PENDING) return WEEKEND_CHANGE_PENDING;
  return SAVE_FAILED;
}

export const FAULT_MESSAGE = 'We could not load your family just now — the setup steps still work below.';

/** One sentence the family can act on, for a tenant lookup that failed. */
export function tenantMessage(error) {
  if (!(error instanceof ApiError)) return FAULT_MESSAGE;
  // A 401 here means the session went away between landing and loading. Saying "we could not load
  // your family" would send them hunting for a fault that is really just a signed-out session.
  if (error.status === 401 || error.status === 403) {
    return 'Your sign-in has expired — please sign in again to pick up where you left off.';
  }
  return FAULT_MESSAGE;
}

/**
 * Turn what the family touched on step 3 into the patch `PATCH /api/tenants/current` receives.
 *
 * Exported so it can be tested for what it is: the rule about what gets SENT. Testing it through
 * the rendered screen is not possible today, because `/onboarding` renders its EMPTY state for a
 * real family — see the note on this module's default export.
 *
 * A choice the family did not touch is deliberately absent from the patch. `null` here means
 * "they never picked", and sending a default for it would overwrite a setting they may have
 * chosen on another screen with a value they never saw.
 *
 * `onboardingCompleted: true` is always present, because reaching this function means they
 * pressed the ceremony button. The server preserves the FIRST timestamp on every later save.
 *
 * @param {{paydayDay: number|null, approvalRequired: boolean|null, weekendGrace: boolean|null}} chosen
 * @returns {object} the patch body
 */
export function buildSettingsPatch({ paydayDay, approvalRequired, weekendGrace } = {}) {
  const patch = { onboardingCompleted: true };
  if (paydayDay !== null && paydayDay !== undefined) {
    patch.paydayPeriod = paydayPeriodFromDayNumber(paydayDay);
  }
  if (approvalRequired !== null && approvalRequired !== undefined) {
    patch.approvalRequired = approvalRequired;
  }
  if (weekendGrace !== null && weekendGrace !== undefined) {
    patch.weekendGrace = weekendGrace;
  }
  return patch;
}

/**
 * The two reads the screen can do WITHOUT, side by side. Each failure is swallowed into its own
 * empty list, so neither can cost the wizard or reach `error`. The pack failure is also REPORTED
 * — as the error itself, not a flag, because the deck's sentence depends on WHY (a 403 is not a
 * transient fault). The member failure is not reported, because the recap has no sentence for it.
 *
 * @returns {Promise<{kids: Array<object>, packs: Array<object>, packsFailed: unknown|false}>}
 */
async function readExtras() {
  // Each read runs inside its own async function, so a seam that throws SYNCHRONOUSLY (not just
  // one that rejects) is still caught here rather than escaping as an unhandled rejection. The
  // reads are thunks, so even LOOKING UP the seam happens inside the try — a test double that
  // lacks an export throws on that lookup, and the old inline try/catch swallowed exactly that.
  const swallow = async (read, shape) => {
    try {
      return { list: shape(await read()), failed: false };
    } catch (caught) {
      return { list: [], failed: caught ?? true };
    }
  };
  const [members, catalog, questList] = await Promise.all([
    swallow(() => fetchFamilyMembers(), toKids),
    swallow(() => fetchQuestPacks(), toPacks),
    // AD-04 (#576 o6). Swallowed like the others: a family who cannot READ their quest list must
    // still be able to add one, and the preview is the part that goes quiet, not the wizard.
    swallow(() => fetchFamilyQuests(), toQuests),
  ]);
  return {
    kids: members.list,
    packs: catalog.list,
    packsFailed: catalog.failed,
    quests: questList.list,
    questsFailed: questList.failed,
  };
}

/**
 * **What is live and what is still not — stated rather than implied.**
 *
 * This route used to render `Onboarding`'s EMPTY state for every real family, because nothing
 * supplied packs. Slice B3 (#26) supplies them from `GET /api/quest-packs`, so a family whose
 * catalog names at least one pack sees the wizard, can reach the ceremony button, and the F-30
 * save below is reachable. Nothing here imports `app/src/fixtures/onboardingFixture.jsx`; the
 * fixture stays a fixture (F-13).
 *
 * Every toggle is ONE `PUT /api/quest-packs/:key/selection` carrying the pack's own `revision`
 * (a bigint STRING, carried verbatim) and a fresh `mutationId`. The screen shows what the SERVER
 * stored: a 200's `selection`, or on a 409 the `details.selection` it hands back, with one sentence
 * saying the click did not take. Nothing retries, nothing loops. A pack the server marks
 * `available: false` is disabled in the deck and refused here too.
 *
 * One PUT per pack at a time. While a pack's PUT is in flight the pack carries `pending: true`:
 * the deck holds its toggle and leaves its optimistic mirror alone, so another pack's answer
 * cannot revert it (the B3 review reproduced exactly that, F-4), and a second click cannot fire a
 * second PUT at the same revision. The ceremony button is held while any pack save pends, and the
 * finish handler checks the same lock, so a celebration cannot run over an unconfirmed choice.
 * Different packs still save independently. What this does NOT do: queue a second intent for a
 * pack mid-save (the design's coalesced-intent queue) — the click is held instead.
 *
 * A pack read that FAILS costs the packs and never the wizard, and it is not silent: the deck is
 * told (`packsError`) and says so in one sentence, and the screen is not mistaken for "nothing to
 * set up". Step 2's lead line counts the picks the deck shows and promises nothing the server does
 * not do (F-13 — the fixture-era "two packs were added for you" is gone).
 *
 * Still NOT wired, so nobody reads this and assumes otherwise:
 *
 * - **The EMPTY state is unreachable on the live route.** The service answers 503
 *   `PACK_CATALOG_UNAVAILABLE` when the global catalog is empty (`quest-packs.service.js`, the
 *   `pack_total === 0` check), so a 200 always carries at least one pack and a failed read is not
 *   "nothing to set up". The empty state survives only for the fixture gallery. (The B3 review
 *   corrected an earlier version of this note that described a `packs: []` 200.)
 * - **Nothing turns a picked pack into quests.** `GET /api/quest-packs` reports
 *   `selectionMode: 'selection-only'`, and `tenant_quest_packs` is read and written by
 *   `quest-packs.service.js` alone — no job, no finish step materialises quests from it. The
 *   quest preview and the sample-cleared message in `Onboarding.jsx` now SAY that (F-13); the
 *   quests step itself is not wired.
 * - **The fixture's sample-data note** stays unreachable from here (B3 review F-2). The LIVE sample
 *   week (#577 L19) is `SampleWeekPanel`, passed as `sampleWeek`: the family chooses "Keep a sample
 *   week" or "Start clean", and the one-tap clear goes through `DELETE /api/onboarding/samples`.
 * - **Toggle failures share the wizard's `error` channel**, so a 409 sentence renders under
 *   "A small hiccup" with the reload button (B3 review F-8). The reload re-reads server truth,
 *   which is a correct recovery, but the heading and the button's implied "retry the change" are
 *   loose. The design wants per-card Saving/Saved/error live regions; that is the
 *   design-alignment slice, not this one.
 * - **The kids step's edit action** has no endpoint.
 */
export default function OnboardingRoute() {
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  // Slice A (#26). A FAILED member read costs the recap, never the wizard, so it has its own
  // state and never touches `error`.
  const [kids, setKids] = useState([]);
  // Slice B3 (#26). Same rule as the members: a FAILED pack read costs the packs, never the
  // wizard, so it has its own state and never touches `error`.
  const [packs, setPacks] = useState([]);
  // The error that read FAILED with, or `false`. Kept apart from `error` (which is the wizard's
  // own fault channel and would show a "hiccup" banner over a wizard that works) and handed to the
  // deck as a sentence, so step 2 can say why it is empty rather than being silently empty.
  const [packsFailed, setPacksFailed] = useState(false);
  // The packs whose PUT is in flight, held SYNCHRONOUSLY in a ref so two clicks in one tick cannot
  // both pass the check (state would still show neither). Mirrored onto each pack as `pending`
  // for the deck, which holds that pack's toggle and does not re-seed its mirror (B3 review F-4).
  const pendingRef = useRef(new Set());
  // Which READ the current pack list came from. Bumped by every load. A toggle captures it before
  // its PUT and compares after: an answer from a superseded read is DROPPED, never published,
  // because the reload already showed the family fresher truth. The reload also clears the lock,
  // so the lock cannot outlive the data it guards (recheck R-1: a lock that survived a reload left
  // a toggle enabled but dead, then wrote a stale revision over the fresh one).
  const loadGenRef = useRef(0);
  // Which READ the quest list came from (#577 review H-1). Separate from `loadGenRef` on purpose:
  // the re-read after a sample keep or clear must beat an OLDER full load that is still in flight,
  // without stopping that load from publishing the kids and the packs. Every quest read claims the
  // next number when it STARTS; only the newest claim may publish.
  const questGenRef = useRef(0);
  // Whether the settings save is in flight; drives the ceremony button's busy state (F-9).
  const [finishing, setFinishing] = useState(false);
  // #577 L19. True while the sample-week panel's keep or clear is in flight; holds the ceremony.
  const [samplePending, setSamplePending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // The three choices step 3 collects, held as ONE value rather than three.
  //
  // Each starts as `null` so the screen can show the family's OWN saved answer once it arrives,
  // rather than a guess that overwrites it on finish.
  //
  // They are one object because they are PERSISTED together (O-onboarding-finish, #576 o19), and a
  // handler that read the other two out of its render closure lost them. Two choices made inside
  // one React batch share a closure, so the second write carried the first choice as `null` and
  // erased it — caught by the refresh test in `test/onboarding-save-wiring.test.jsx`, not reasoned
  // about. One object means the write happens inside the updater, where `prev` is the truth. That
  // is the shape `patchChildDraft` below already uses.
  const [choices, setChoices] = useState(EMPTY_HOST_DRAFT);
  const { paydayDay, paydayType, approvalRequired, weekendGrace } = choices;

  // AD-04. Starts EMPTY and is loaded by an effect once the tenant id arrives, NOT seeded on the
  // first render. The draft key carries the tenant id (`childDraft.js` rule 4), and the tenant is
  // not known until `fetchTenant` answers — so seeding synchronously would have to read an
  // unscoped key, which is the leak the scope exists to close. The cost is one frame of empty
  // fields on a remount, and that is the right trade against handing one family another's draft.
  const [childDraft, setChildDraft] = useState(EMPTY_CHILD_DRAFT);
  const [addingChild, setAddingChild] = useState(false);
  const [addChildNotice, setAddChildNotice] = useState(null);

  // AD-04's quest half (#576 o6). Same shape as the child block above, for the same reasons: the
  // draft starts EMPTY and is loaded by an effect once the tenant id is known, because the key
  // carries that id and seeding synchronously would have to read an unscoped one.
  const [quests, setQuests] = useState([]);
  const [questsFailed, setQuestsFailed] = useState(false);
  const [questDraft, setQuestDraft] = useState(EMPTY_QUEST_DRAFT);
  const [addingQuest, setAddingQuest] = useState(false);
  const [addQuestNotice, setAddQuestNotice] = useState(null);

  // Which family's draft has been loaded. Held in a ref rather than state because it must not cause
  // a render, and because the effect below reads it in the same tick it writes it.
  const loadedDraftTenantRef = useRef(null);

  // The same, for the step-3 host draft (O-onboarding-finish). A SEPARATE ref, because the two
  // drafts are restored independently and sharing one would tie each to the other's load.
  const loadedHostTenantRef = useRef(null);

  // The same, for the quest draft. Its own ref, so one draft's load cannot gate another's.
  const loadedQuestTenantRef = useRef(null);

  // Load THIS family's draft once their id is known. It never overwrites something already typed:
  // a parent who started typing before `fetchTenant` answered keeps what they typed.
  useEffect(() => {
    const tenantId = tenant?.id;
    if (!tenantId || loadedDraftTenantRef.current === tenantId) return;
    loadedDraftTenantRef.current = tenantId;
    const stored = readChildDraft(tenantId);
    setChildDraft((current) => {
      const typed = ['name', 'username', 'birthDate'].some((f) => (current[f] || '').trim() !== '');
      return typed ? current : stored;
    });
  }, [tenant?.id]);

  // The quest draft, same rule: never overwrite something already typed (AD-04, #576 o6).
  useEffect(() => {
    const tenantId = tenant?.id;
    if (!tenantId || loadedQuestTenantRef.current === tenantId) return;
    const previousTenantId = loadedQuestTenantRef.current;
    loadedQuestTenantRef.current = tenantId;
    const stored = readQuestDraft(tenantId);
    setQuestDraft((current) => {
      // A DIFFERENT family on the same mounted screen takes their own draft WHOLE. Keeping what is
      // on screen would show one family the other's text and could submit it into their account —
      // the same hole closed on the host draft in #628, filed again here by the cross-model review.
      if (previousTenantId !== null && previousTenantId !== tenantId) return stored;
      // The FIRST load keeps what was typed before the tenant arrived, and writes it through to
      // this family's key — which the typing itself could not do, because the key needs the id.
      const typed = (current.name || '').trim() !== '';
      if (!typed) return stored;
      writeQuestDraft(tenantId, current);
      return current;
    });
  }, [tenant?.id]);

  /**
   * Restore THIS family's step-3 choices once their id is known (O-onboarding-finish, #576 o19).
   *
   * Only a field the family has NOT already touched this mount is restored. `null` is what
   * "never picked" means for all three, so the test is `=== null` and never a truthiness test:
   * Sunday is `0` and "no approval needed" is `false`, and both are choices.
   *
   * This effect never WRITES. Persisting belongs to the three handlers below, which run only when
   * the family changes something — so a restore can never be mistaken for a choice.
   */
  useEffect(() => {
    const tenantId = tenant?.id;
    if (!tenantId || loadedHostTenantRef.current === tenantId) return;
    const stored = readHostDraft(tenantId);
    const previousTenantId = loadedHostTenantRef.current;
    loadedHostTenantRef.current = tenantId;
    setChoices((current) => {
      // A DIFFERENT family on the same mounted screen takes their own draft WHOLE. Merging would
      // leave the previous family's choices in place, show them to this one, and write the mixed
      // object back under this family's key — filed by the cross-model review of this diff.
      //
      // No code path reaches this today: all three `setTenant` calls take the session's own
      // family (`fetchTenant` / `updateTenant`), so the id cannot move between two different
      // families without a remount. It is five lines against a family seeing another family's
      // settings, so it is written rather than argued about.
      if (previousTenantId !== null && previousTenantId !== tenantId) return { ...stored };
      const next = { ...current, paydayType: current.paydayType ?? stored.paydayType };
      for (const field of Object.keys(EMPTY_HOST_DRAFT)) {
        if (next[field] === null) next[field] = stored[field];
      }
      return next;
    });
  }, [tenant?.id]);

  /**
   * Persist the three choices as the family makes them, so a refresh before the ceremony button
   * keeps them. One handler per choice, each writing the WHOLE draft.
   *
   * **An effect was tried first and is wrong, measured rather than reasoned.** An effect on
   * `[paydayDay, approvalRequired, weekendGrace]` also fires on the render that RESTORES them, and
   * it reads that render's closure — three `null`s. `writeHostDraft` turns a draft with nothing in
   * it into a CLEAR, so every mount removed the key and wrote it back a render later. The end
   * state looked right, which is why only a spy on `removeItem` could see it
   * (`test/onboarding-save-wiring.test.jsx`), and a tab closed inside that window lost the draft.
   * Guarding the effect on "has the restore run" did not help: the restore sets its ref in the
   * same tick, so the guard was already open.
   *
   * A handler cannot have that problem. It runs only when the family actually changes something,
   * and it never runs during a restore. This is also the shape `childDraft.js` already uses, which
   * is the precedent this repo asks for.
   */
  const patchChoices = useCallback(
    (patch) => {
      setChoices((current) => {
        const next = { ...current, ...patch };
        // Written INSIDE the updater, where `current` is the real previous value even when two
        // choices land in one React batch. Writing outside it reads the render closure, which is
        // how the second of two clicks erased the first.
        writeHostDraft(tenant?.id, next);
        return next;
      });
    },
    [tenant?.id],
  );

  const choosePayday = useCallback((day) => patchChoices({ paydayDay: day, paydayType: 'weekly' }), [patchChoices]);
  const choosePaydayType = useCallback((type) => patchChoices({ paydayType: type }), [patchChoices]);

  // #580 4a3 (I9): with the change flow on, a different payday is changed FIRST, through the
  // warning, and only then the other settings. Off or unknown, finishing is exactly today's PATCH.
  const actor = useOptionalActor()?.actor ?? null;
  const onChangeCommitted = useCallback((period) => {
    setTenant((prev) => (prev ? { ...prev, paydayPeriod: period } : prev));
  }, []);
  // Parents only, as in Settings: a child never sends the probe (the route would refuse it anyway).
  const change = usePaydayChange({
    tenantId: actor?.isAdmin === true ? tenant?.id : undefined,
    actorId: actor?.id, savedPeriod: tenant?.paydayPeriod, onCommitted: onChangeCommitted,
  });
  const awaitingPayday = change.state.phase === 'warning' || change.state.phase === 'sending';
  const chooseApproval = useCallback(
    (required) => patchChoices({ approvalRequired: required }),
    [patchChoices],
  );
  const chooseWeekendGrace = useCallback(
    (grace) => patchChoices({ weekendGrace: grace }),
    [patchChoices],
  );

  /**
   * Publish one `readExtras` answer. ONE function, because there are two callers and they must
   * agree: `load`, which the retry button uses, and the mount effect, which every page load uses.
   *
   * They did not agree. Only `load` published the quest list, so a family who already had quests
   * saw "The quest list is empty" on every page load until they pressed retry — measured on dev at
   * `fc75f4f`, where the server held two quests and `GET /api/admin/chores` answered 200. One line
   * added to one of two copies that had to match. Now there is one copy.
   */
  const publishExtras = useCallback((extras, questGen) => {
    setKids(extras.kids);
    setPacks(extras.packs);
    setPacksFailed(extras.packsFailed);
    // A quest read that started after this one (the post-change re-read) has already published a
    // newer list. This one's quests are older, so they are dropped; the rest still publishes.
    if (questGen === questGenRef.current) {
      setQuestsFailed(Boolean(extras.questsFailed));
      if (!extras.questsFailed) setQuests(extras.quests);
    }
  }, []);

  const load = useCallback(async () => {
    const gen = ++loadGenRef.current;
    const questGen = ++questGenRef.current;
    setError(null);
    // A retry starts clean: a stale "could not load the packs" must not outlive the reload (F-14).
    setPacksFailed(false);
    setLoading(true);
    try {
      setTenant(await fetchTenant());
    } catch (caught) {
      setError(tenantMessage(caught));
    } finally {
      setLoading(false);
    }
    // Deliberately AFTER the tenant, and deliberately swallowed. The family name is what the
    // screen cannot do without; the recap and the deck are not.
    const extras = await readExtras();
    // A newer load has taken over; this one's answer would be the stale one.
    if (gen !== loadGenRef.current) return;
    // The fresh list is the truth now. Any PUT still in flight belongs to the old list: its lock is
    // released here and its answer will be dropped by the generation check in the toggle.
    pendingRef.current.clear();
    publishExtras(extras, questGen);
  }, [publishExtras]);

  useEffect(() => {
    let alive = true;
    // The alive flag exists so a family who navigates away mid-request does not get a setState on
    // an unmounted tree. `load` is re-used by the retry, where the guard is not needed.
    const gen = ++loadGenRef.current;
    const questGen = ++questGenRef.current;
    (async () => {
      setError(null);
      setLoading(true);
      try {
        const next = await fetchTenant();
        if (alive) setTenant(next);
      } catch (caught) {
        if (alive) setError(tenantMessage(caught));
      } finally {
        if (alive) setLoading(false);
      }
      const extras = await readExtras();
      if (alive && gen === loadGenRef.current) {
        pendingRef.current.clear();
        publishExtras(extras, questGen);
      }
    })();
    return () => {
      alive = false;
    };
    // `publishExtras` is stable (`useCallback` with an empty list), so naming it here satisfies the
    // rule without turning this into an effect that re-runs and re-fetches on every render.
  }, [publishExtras]);

  /**
   * The family's SAVED payday, as a `DayPicker` number.
   *
   * `null` covers two different families, and both must land on the same screen state: one who
   * settles up monthly (`calendar_month` has no weekday, so the mapping answers `null`) and one
   * whose tenant has not loaded. Neither may be shown a weekday they did not choose.
   */
  const savedPaydayDay = tenant?.paydayPeriod ? dayNumberFromPaydayPeriod(tenant.paydayPeriod) : null;
  const savedPaydayType = PAYDAY_PERIODS.includes(tenant?.paydayPeriod) ? paydayKind(tenant.paydayPeriod) : null;

  /**
   * Which of the three steps the family stands on: 0 ages, 1 packs, 2 payday.
   *
   * **This route never passed it, and the cost was total.** `Onboarding.jsx` defaults `activeStep`
   * to `1` and sets `step3Locked = active < 2`, so step 3 was "up next" forever and its DayPicker
   * and both switches were DISABLED for every real family. Measured on the dev app at `1639aa1`,
   * after a real signup and a real pack save that returned 200: all seven day buttons reported
   * `disabled: true`. The payday, approval and weekend-grace choices could not be made at all, so
   * `handleFinish` could only ever send `onboardingCompleted: true`.
   *
   * The same default also told a family with NO kids that the ages step was already done, which is
   * the false progress this screen's own criteria forbid.
   *
   * The order is the strip's own (`Onboarding.jsx` prop docs), and each rung is a fact about the
   * account rather than a click this screen remembers — so a refresh cannot walk the family
   * backwards.
   *
   * KNOWN GAP, named rather than papered over: a family who adds neither a kid nor a pack stays
   * before the payday step. Closing that needs a product answer about whether either step may be
   * skipped outright, and it is a smaller trap than the one this replaces, where NOBODY reached
   * payday.
   */
  const activeStep = packs.some((pack) => pack.added === true) ? 2 : kids.length > 0 ? 1 : 0;

  /**
   * Save the three choices step 3 collected, then take the family to Today.
   *
   * It RESOLVES only on a real save. `Onboarding.handleFinish` awaits this before celebrating, so
   * a rejection is what keeps "First lantern lit" from firing over choices that were lost — the
   * same clause the premature-celebration repair established.
   *
   * The same rule holds the way out (#639). This button used to save and then leave the family on
   * `#/onboarding` with nowhere to go. Now it navigates to Today, and only after every write below
   * has resolved: a rejection throws before the navigation, so a family whose save failed stays
   * here, reads why, and can press again. The navigation REPLACES this entry, so Back does not
   * return a finished family to the setup screen, and it carries no state: Today asks the server.
   *
   * `onboardingCompleted: true` rides along. The server preserves the first timestamp on every
   * later save, so a family who comes back through this screen does not have their setup date
   * rewritten.
   *
   * A choice the family did not touch is deliberately NOT sent. Sending a default would overwrite
   * a setting they had already chosen on another screen with a value they never picked.
   */
  const handleFinish = useCallback(async () => {
    // Design, "Finish precondition": never celebrate over a pack choice that has not been
    // confirmed. The button is held while a pack save pends; this is the check behind it, so a
    // programmatic or keyboard press cannot bypass the hold. Throwing keeps the celebration off.
    if (pendingRef.current.size > 0) {
      setError(PACK_SAVE_PENDING);
      throw new Error(PACK_SAVE_PENDING);
    }
    if (samplePending) {
      setError(SAMPLE_CHANGE_PENDING);
      throw new Error(SAMPLE_CHANGE_PENDING);
    }
    if (change.unresolved) {
      setError(PAYDAY_NOT_DONE);
      throw new Error(PAYDAY_NOT_DONE);
    }
    setFinishing(true);
    try {
      setError(null);
      // Built INSIDE the try on purpose. `buildSettingsPatch` throws for a day it cannot map, and
      // outside the try that throw skipped `setError` entirely: `Onboarding.handleFinish` caught
      // the rejection, so the family got no celebration AND no sentence — a click that does
      // nothing at all. Not reachable today, because `DayPicker` in single-select mode only ever
      // emits 0-6, so this carries no test rather than a vacuous one.
      const chosen = chosenPaydayPeriod({ paydayDay, paydayType });
      const patch = buildSettingsPatch({ paydayDay: null, approvalRequired, weekendGrace });
      // The selected type owns the payday field; a remembered weekday cannot override it.
      if (chosen !== null && (change.mode !== 'on' || chosen !== tenant?.paydayPeriod)) patch.paydayPeriod = chosen;
      let viaChange = change.mode === 'on' && chosen !== null && chosen !== tenant?.paydayPeriod;
      if (!viaChange) {
        try {
          // Publish the SERVER's answer, never the patch that was sent, so a value it clamped or
          // refused is never shown back as saved.
          setTenant(await updateTenant(patch));
        } catch (caught) {
          // Switched on since this screen looked. The refusal wrote no field of the mixed patch.
          if (codeOf(caught) !== CONFIRMATION_REQUIRED || chosen === null) throw caught;
          viaChange = true;
        }
      }
      if (viaChange) {
        // I9: the payday change commits first. If it does not, nothing else is written and setup
        // stays open. The panel says what happened; the error line only says setup is not done.
        const outcome = await change.propose(chosen);
        if (outcome === 'shown') {
          await change.awaitDecision().catch(() => { throw Object.assign(new Error(PAYDAY_NOT_DONE), { userMessage: PAYDAY_NOT_DONE }); });
        } else if (outcome === 'refused') {
          throw Object.assign(new Error(PAYDAY_NOT_DONE), { userMessage: PAYDAY_NOT_DONE });
        }
        // Then the rest WITHOUT the payday: a failure here keeps the new payday, and the retry
        // sends only this PATCH, because the saved payday now equals the choice. `legacy` means
        // the server said the flow is off after all, so it is today's mixed PATCH.
        setTenant(await updateTenant(outcome === 'legacy' ? patch : buildSettingsPatch({ paydayDay: null, approvalRequired, weekendGrace })));
      }
      // The choices are SAVED now, so the draft has done its job and must not outlive it.
      //
      // The three local copies go back to `null` in the same breath, which hands authority to the
      // tenant row the server just answered with: the step-3 props fall through `??` to it, so the
      // family still sees exactly what they chose, and there is no second copy left to drift.
      clearHostDraft(tenant?.id);
      setChoices({ ...EMPTY_HOST_DRAFT });
    } catch (caught) {
      setError(caught?.userMessage || saveMessage(caught));
      // #583 unit 6: tomorrow's weekend rule is already written and nothing was saved, so the
      // refused choice goes, draft included. Another tab may have changed the rule since load:
      // refresh the tenant before falling back to it. A failed read preserves the draft and says so.
      if (codeOf(caught) === WEEKEND_RULE_PENDING) {
        try {
          setTenant(await fetchTenant());
          patchChoices({ weekendGrace: null });
        } catch {
          setError(`${WEEKEND_CHANGE_PENDING} We couldn't check the saved rule. Reload to see it.`);
        }
      }
      // Rethrowing is the point. A swallowed error here resolves the promise and celebrates.
      throw caught;
    } finally {
      // `finally`, not the catch: the save rethrows on failure and must still release the button.
      setFinishing(false);
    }
    // Reached only when every write above resolved; a failure rethrew out of the catch (#639).
    navigate(LANDING_ROUTE, { replace: true });
    // `tenant?.id` is load-bearing, not incidental: it names the draft key to clear. A stale one
    // would clear some other family's draft and leave this family's behind.
  }, [approvalRequired, change, navigate, patchChoices, paydayDay, paydayType, samplePending, tenant?.id, tenant?.paydayPeriod, weekendGrace]);

  /**
   * Re-read the quest list after the sample week changed (#577 L19), so the examples appear with
   * their label, or leave. A FAILED read keeps the last confirmed list rather than publishing an
   * empty one.
   *
   * It CLAIMS a quest generation rather than reading the current one (review H-1): a full load
   * already in flight read its quests before the change committed, and matching its number let it
   * republish that older list over this one. A full load started AFTER this read still wins.
   */
  const reloadQuests = useCallback(async () => {
    const gen = ++questGenRef.current;
    try {
      const next = toQuests(await fetchFamilyQuests());
      if (gen === questGenRef.current) {
        setQuests(next);
        setQuestsFailed(false);
      }
    } catch {
      if (gen === questGenRef.current) setQuestsFailed(true);
    }
  }, []);

  /**
   * Add ONE quest to the family's account (AD-04, "one-quest setup", #576 o6).
   *
   * The chip is published ONLY after the server answers. An optimistic row would tell a family a
   * quest exists when nothing was written, which is the false progress this criterion forbids.
   *
   * The draft is cleared on BOTH outcomes that mean the quest is on the account — the write, and
   * the replay that found it already there — and kept on every failure, so a retry does not start
   * from nothing.
   */
  const handleAddQuest = useCallback(async () => {
    if (addingQuest) return;
    const name = (questDraft.name || '').trim();
    if (name.length < 2) {
      setAddQuestNotice({ tone: 'warn', message: ADD_QUEST_NEEDS_NAME });
      return;
    }
    setAddingQuest(true);
    setAddQuestNotice(null);
    try {
      const { quest, replayed } = await createFamilyQuest({
        name,
        periodType: questDraft.periodType,
        // #577 row 3. Only when the family chose one; otherwise the column's default decides.
        ...(typeof questDraft.reward === 'number' ? { reward: questDraft.reward } : {}),
      });
      // The SERVER's row, never the draft that was sent.
      const title = (quest && quest.name) || name;
      if (quest) {
        // A replay answers with the row that already exists, so the same quest must not appear
        // twice in the preview.
        setQuests((current) =>
          current.some((row) => row.id === quest.id) ? current : [...current, ...toQuests([quest])],
        );
      }
      // A load still in flight holds a list from BEFORE this quest existed. Publishing it would
      // erase the row just added while the "it is on your list" sentence stayed on screen. Bumping
      // the generation makes that load discard its own answer, through the guard already in
      // `load`. A retry is a button the family can press again; a vanishing quest is not.
      loadGenRef.current += 1;
      questGenRef.current += 1;
      setQuestDraft({ ...EMPTY_QUEST_DRAFT });
      clearQuestDraft(tenant?.id);
      setAddQuestNotice(
        replayed
          ? { tone: 'info', message: ADD_QUEST_ALREADY }
          : { tone: 'info', message: addQuestSaved(title) },
      );
    } catch (caught) {
      // The draft is deliberately NOT cleared on EITHER outcome: nothing is known to be saved, and
      // a family who retypes a quest they already typed is a family the failure cost twice.
      //
      // The two outcomes get DIFFERENT sentences, because a family can do different things about
      // them. A fault says nothing was saved and invites a retry. An accepted write with no row
      // back must not say that — it may well have landed — so it asks them to look.
      setAddQuestNotice({
        tone: 'warn',
        message:
          questErrorCode(caught) === QUEST_UNCONFIRMED ? ADD_QUEST_UNCONFIRMED : ADD_QUEST_FAILED,
      });
    } finally {
      setAddingQuest(false);
    }
  }, [addingQuest, questDraft.name, questDraft.periodType, questDraft.reward, tenant?.id]);

  /**
   * #577 row 3 — reviewing a starting quest's reward.
   *
   * The edit lives here, like every draft on this screen. The list row changes ONLY when the server
   * confirms the new amount, and it takes the SERVER's number: a row showing an amount nobody saved
   * is the false progress AD-04 forbids. A failure keeps the edit open, so a retry is one press.
   */
  const [questEdit, setQuestEdit] = useState(null);
  const [savingQuestEdit, setSavingQuestEdit] = useState(false);
  const rewardSaving = useRef(false);
  const [questEditNotice, setQuestEditNotice] = useState(null);

  const handleEditQuest = useCallback((quest) => {
    if (rewardSaving.current || !quest || !Number.isInteger(quest.id)) return;
    setQuestEdit({
      id: quest.id,
      title: quest.title,
      // `null`, never 0: a row with no amount opens in the stepper's neutral state. An invented 0
      // would be one Save away from writing $0.00 over the family's own amount.
      reward: typeof quest.reward === 'number' ? quest.reward : null,
    });
    setQuestEditNotice(null);
  }, []);

  const patchQuestEdit = useCallback((patch) => {
    if (rewardSaving.current) return;
    setQuestEdit((current) => (current ? { ...current, ...patch } : current));
  }, []);

  const cancelQuestEdit = useCallback(() => {
    if (rewardSaving.current) return;
    setQuestEdit(null);
    setQuestEditNotice(null);
  }, []);

  const saveQuestEdit = useCallback(async () => {
    if (rewardSaving.current || !questEdit) return;
    rewardSaving.current = true;
    const edit = questEdit;
    setSavingQuestEdit(true);
    setQuestEditNotice(null);
    try {
      const row = await updateFamilyQuestReward(edit.id, edit.reward);
      const reward = Number(row.reward);
      questGenRef.current += 1;
      setQuests((current) => current.map((q) => (q.id === edit.id ? { ...q, reward } : q)));
      setQuestEdit(null);
      setQuestEditNotice({ tone: 'info', message: questRewardSaved(row.name || edit.title, reward) });
    } catch (caught) {
      setQuestEditNotice({ tone: 'warn', message: questRewardFailedMessage(caught) });
    } finally {
      rewardSaving.current = false;
      setSavingQuestEdit(false);
    }
  }, [questEdit]);

  /**
   * Publish ONE pack's selection as the server reported it. Always a new array, because the deck
   * adopts the host's state only when the `packs` prop changes identity — that is how an
   * optimistic click that the server overruled snaps back.
   */
  const publishSelection = useCallback((id, selection) => {
    setPacks((prev) =>
      prev.map((p) =>
        p.id === id
          ? {
              ...p,
              added: selection.added === true,
              revision: typeof selection.revision === 'string' ? selection.revision : p.revision,
              // Publishing an answer is what ends the in-flight state for the deck.
              pending: false,
            }
          : p,
      ),
    );
  }, []);

  /** Mark one pack as saving, for the deck (its toggle is held, its mirror is left alone). */
  const markPending = useCallback((id) => {
    setPacks((prev) => prev.map((p) => (p.id === id ? { ...p, pending: true } : p)));
  }, []);

  /**
   * One toggle, one PUT, and the SERVER's answer is what the family sees.
   *
   * `expectedRevision` is the pack's own revision from the last thing the server said — carried
   * verbatim, never parsed. On 200 the returned `selection` is published, never the optimistic
   * `next`. On a 409 the server hands back the CURRENT selection in `details.selection`; the deck
   * is reconciled to it and the family is told, in one sentence, that the click did not take.
   * There is no retry and no loop: a stale click is a fact to show, not a race to win.
   *
   * Any other failure puts the click back to the last known state and explains it through the
   * same `error` channel a failed finish uses.
   *
   * @param {*} id the pack's key
   * @param {boolean} next the state the family asked for
   */
  const handleTogglePack = useCallback(
    async (id, next) => {
      const pack = packs.find((p) => p.id === id);
      if (!pack) return;
      // One PUT per pack at a time. The deck disables the button while a save pends; this is the
      // synchronous lock behind it, so a second click in the same tick cannot fire a second PUT at
      // the same expectedRevision (B3 review F-4).
      if (pendingRef.current.has(id)) return;
      if (pack.available === false) {
        // The deck disables this button; this is the second lock. Republish so the mirror snaps
        // back if the click got through anyway.
        publishSelection(id, pack);
        setError(unavailableSentence(pack.unavailableReason));
        return;
      }
      pendingRef.current.add(id);
      markPending(id);
      // The read this click belongs to. If a reload replaces the list while the PUT is out, the
      // answer is for data the family no longer sees: it is dropped, and the lock is NOT touched,
      // because the reload already released it and a newer PUT for this pack may now own it.
      const gen = loadGenRef.current;
      try {
        setError(null);
        const selection = await setQuestPackSelection(id, {
          added: next,
          expectedRevision: pack.revision,
          mutationId: newMutationId(),
        });
        if (gen !== loadGenRef.current) return;
        pendingRef.current.delete(id);
        publishSelection(id, selection);
      } catch (caught) {
        if (gen !== loadGenRef.current) return;
        pendingRef.current.delete(id);
        // A 409 says what IS stored; anything else means the last thing the server said still is.
        publishSelection(id, conflictSelection(caught) ?? pack);
        setError(toggleMessage(caught));
      }
    },
    [markPending, packs, publishSelection],
  );

  /**
   * Publish one edit of the add-a-child form, and persist the whole draft. (AD-04.)
   *
   * The sign-in name FOLLOWS the child's name until the family edits it, and stops following the
   * moment they do. Keeping it in step afterwards would overwrite a name they chose on purpose,
   * which is the "editable" half of "editable suggestions" — a suggestion that cannot be kept is
   * not editable.
   */
  const patchChildDraft = useCallback(
    (patch) => {
      setChildDraft((current) => {
        const next = { ...current, ...patch };
        if (
          Object.prototype.hasOwnProperty.call(patch, 'name') &&
          !Object.prototype.hasOwnProperty.call(patch, 'username') &&
          !next.usernameEdited
        ) {
          next.username = suggestUsername(next.name);
        }
        writeChildDraft(tenant?.id, next);
        return next;
      });
    },
    [tenant?.id],
  );

  /**
   * Publish one quest field and persist the whole draft (AD-04, #576 o6).
   *
   * The write lives INSIDE the updater, where `current` is the real previous value even when two
   * fields change in one React batch. Writing outside it reads the render closure, which is how the
   * second of two changes erased the first on the host draft — measured, not reasoned.
   */
  const patchQuestDraft = useCallback(
    (patch) => {
      setQuestDraft((current) => {
        const next = { ...current, ...patch };
        writeQuestDraft(tenant?.id, next);
        return next;
      });
    },
    [tenant?.id],
  );

  /**
   * Add ONE child to the family's account. (AD-04 — "one-child setup", "idempotent writes".)
   *
   * The chip is published ONLY after the server answers 201. An optimistic chip would tell a family
   * their child was saved while the write was still in the air, and a refused write would leave that
   * claim standing until they reloaded — exactly the "failed requests falsely indicate saved
   * progress" this criterion forbids.
   *
   * The draft is cleared ONLY on 201, for the same reason: a family whose write failed keeps every
   * character they typed and can press the button again.
   */
  const handleAddChild = useCallback(async () => {
    const name = (childDraft.name || '').trim();
    const username = (childDraft.username || '').trim();
    const birthDate = (childDraft.birthDate || '').trim();

    // Checked HERE rather than by disabling the button: a disabled control that will not say why is
    // a dead end for anyone who cannot see which field is empty.
    if (name.length < 2) {
      setAddChildNotice({ tone: 'setback', message: ADD_CHILD_NEEDS_NAME });
      return;
    }
    if (!username) {
      setAddChildNotice({ tone: 'setback', message: ADD_CHILD_NEEDS_USERNAME });
      return;
    }
    if (!birthDate) {
      setAddChildNotice({ tone: 'setback', message: ADD_CHILD_NEEDS_BIRTHDAY });
      return;
    }

    setAddChildNotice(null);
    setAddingChild(true);
    let conflicted = false;
    let conflict = { elsewhere: false, suggestion: null };
    // Generated HERE rather than inside the API module, because the parent has to be TOLD it, and
    // the sentence that tells them is written below. See `generateInitialPassword` for why a child
    // account needs one at all: the server refuses a non-email account without a password, and a
    // live run on dev earned exactly that 400 the day PR #624 merged.
    const initialPassword = generateInitialPassword();
    try {
      await createFamilyChild({ displayName: name, username, birthDate, initialPassword });
    } catch (caught) {
      // A 409 is the username check in `server/services/admin.service.js` refusing a name this
      // family already uses. It is NOT decided here: the re-read below is what tells us whether the
      // row is the one we just meant to create. Anything else is a fault and ends the attempt.
      if (memberErrorCode(caught) !== MEMBER_EXISTS) {
        setAddChildNotice({ tone: 'setback', message: ADD_CHILD_FAILED });
        setAddingChild(false);
        return;
      }
      conflicted = true;
      conflict = memberConflictDetail(caught);
    }

    // #580: put the server's free name in the box, and stop it following the child's name from here
    // on — the family is about to press the button with it, and a later name edit must not undo it.
    const offerFreeName = () => {
      if (!conflict.suggestion) return;
      setChildDraft((current) => {
        const next = { ...current, username: conflict.suggestion, usernameEdited: true };
        writeChildDraft(tenant?.id, next);
        return next;
      });
    };

    // SERVER TRUTH, ALWAYS — and this is the repair the cross-model review asked for.
    //
    // The first version of this handler published a chip from the POST's own response and cleared
    // the draft on any resolved promise. Two real defects followed from that, both reported and
    // both confirmed: a 201 whose body carried no `user` row cleared the draft and announced the
    // child was saved while showing no chip; and a write that COMMITTED but whose response was lost
    // came back as a 409 on retry, which the old code reported as a conflict and left the family
    // with no chip for a child that existed.
    //
    // Re-reading the member list settles both, because the list is the only thing that actually
    // knows. It costs one round trip, once per child, on a screen a family sees once.
    let members = null;
    try {
      members = await fetchFamilyMembers();
    } catch {
      members = null;
    }
    setAddingChild(false);
    if (Array.isArray(members)) setKids(toKids(members));

    // ANOTHER family holds the name (#580) — unless this family's own list holds it. The server's
    // code means "some account holds it", this family's included: of two creates of one child sent
    // at once, both pass the in-family check and the loser is refused by the cross-family claim,
    // although the holder is the child the winner just made. So a name the list holds falls through
    // to the in-family and replay handling below. Only a list that was READ and lacks the name says
    // "another family"; a list that could not be read says nothing either way (the end of this
    // handler), and offers no name, because pressing Add on one could make a second child.
    if (conflicted && conflict.elsewhere && Array.isArray(members) && !findMemberByUsername(members, username)) {
      offerFreeName();
      setAddChildNotice({ tone: 'setback', message: addChildTakenElsewhere(username, conflict.suggestion) });
      return;
    }

    if (Array.isArray(members)) {
      const match = findMemberByUsername(members, username);
      if (match) {
        // The row is there. Whether this attempt created it or a replay found it, the family's next
        // step is the same, so the draft is cleared and the chip is the server's own.
        const theirName = match.display_name || match.first_name || match.username || name;
        if (!conflicted || theirName.toLowerCase() === name.toLowerCase()) {
          setChildDraft({ ...EMPTY_CHILD_DRAFT });
          clearChildDraft(tenant?.id);
          // The password is shown ONCE, and only on the attempt that actually wrote it. A REPLAY
          // found an existing row, and that child's password is whatever it already was — printing
          // this attempt's freshly generated string there would hand the family a password that
          // signs nobody in.
          setAddChildNotice(
            conflicted
              ? { tone: 'info', message: `${theirName} is in your family.` }
              : { tone: 'info', message: addChildSaved(theirName, username, initialPassword) },
          );
          return;
        }
        // The sign-in name is taken by SOMEBODY ELSE. Naming who makes the next step obvious, and
        // the draft is kept because the family still has a child to add.
        offerFreeName();
        setAddChildNotice({ tone: 'setback', message: addChildTakenBy(theirName, conflict.suggestion) });
        return;
      }
      // No row, and the write was refused as a conflict — a collision with a member this list does
      // not show (a removed account still holds its username). Keep the draft; offer the server's
      // free name if it sent one, else name the one move.
      if (conflicted) {
        offerFreeName();
        setAddChildNotice({ tone: 'setback', message: addChildAlready(conflict.suggestion) });
        return;
      }
      // No row after a write the server ACCEPTED. Never reported as saved, and never as failed
      // either, because the server said 2xx: the family is told what is actually known.
      setAddChildNotice({ tone: 'setback', message: ADD_CHILD_UNCONFIRMED });
      return;
    }

    // The re-read itself failed, so nothing about the child is known. The draft is kept either way,
    // and no free name is offered. An in-family 409 is still the in-family sentence; a
    // `SIGN_IN_NAME_TAKEN` may be this family's own new child or another family's, so it claims
    // neither.
    setAddChildNotice({
      tone: 'setback',
      message: conflicted && !conflict.elsewhere ? ADD_CHILD_ALREADY : ADD_CHILD_UNCONFIRMED,
    });
  }, [childDraft, tenant?.id]);

  /** True while any pack save is in flight; the ceremony button is held for the duration. */
  const packSavePending = packs.some((p) => p.pending === true);

  return (
    <Suspense
      fallback={
        <div style={{ padding: '2rem' }}>
          <LanternLoader label="Lighting the lanterns — loading your setup." />
        </div>
      }
    >
      <Onboarding
        // The family's REAL name. Empty while it loads, which `Onboarding` renders as
        // "Set up your family" — true for everyone, and never another family's name.
        familyName={tenant?.name || ''}
        kids={kids}
        // AD-04. The draft lives HERE, not in the page, because it has to outlive the page: the
        // wizard unmounts on a step change and on the lazy boundary, and a half-typed child must
        // survive both.
        childDraft={childDraft}
        onChildDraftChange={patchChildDraft}
        onAddChild={handleAddChild}
        addingChild={addingChild}
        addChildNotice={addChildNotice}
        activeStep={activeStep}
        quests={quests}
        questsError={questsFailed ? (quests.length ? STALE_LIST : "We couldn't check the quest list just now. Check again to see your quests.") : null}
        onRetryQuests={reloadQuests}
        questDraft={questDraft}
        onQuestDraftChange={patchQuestDraft}
        onAddQuest={handleAddQuest}
        addingQuest={addingQuest}
        addQuestNotice={addQuestNotice}
        onEditQuest={handleEditQuest}
        questEdit={questEdit}
        onQuestEditChange={patchQuestEdit}
        onSaveQuestEdit={saveQuestEdit}
        onCancelQuestEdit={cancelQuestEdit}
        savingQuestEdit={savingQuestEdit}
        questEditNotice={questEditNotice}
        packs={packs}
        onTogglePack={handleTogglePack}
        packsError={packsFailed ? packsMessage(packsFailed) : null}
        subheading={SAVED_ON_FINISH}
        loading={loading}
        error={error}
        onRetry={load}
        paydayDay={paydayDay ?? savedPaydayDay}
        onPaydayChange={choosePayday}
        paydayTypes={change.mode === 'on'}
        paydayType={paydayType ?? savedPaydayType}
        onPaydayTypeChange={choosePaydayType}
        paydayChange={<PaydayChangePanel change={change} />}
        approvalRequired={approvalRequired ?? tenant?.approvalRequired ?? true}
        onApprovalChange={chooseApproval}
        weekendGrace={weekendGrace ?? tenant?.weekendGrace ?? true}
        onWeekendGraceChange={chooseWeekendGrace}
        // F-30. `Onboarding.handleFinish` awaits this and celebrates only when it RESOLVES, so a
        // failed save cannot produce a "First lantern lit" for a family whose choices were lost.
        // It also moves the family to Today, under the same condition (#639).
        onFinish={handleFinish}
        finishing={finishing}
        // #580: while the warning waits for the parent, the button says so instead of "Lighting…".
        {...(finishing && awaitingPayday ? { finishingLabel: PAYDAY_WAITING } : {})}
        finishDisabled={packSavePending || samplePending}
        // Rendered by the deck in its live region while the button is held, so the reason reaches
        // the family without any click (recheck R-2: a disabled button dispatches none).
        finishDisabledReason={packSavePending ? PACK_SAVE_PENDING : SAMPLE_CHANGE_PENDING}
        // #577 L19: "Keep a sample week" / "Start clean", and the one-tap clear. Only once the
        // family is known, because every answer is checked against its id.
        sampleWeek={tenant?.id ? (
          <SampleWeekPanel
            familyId={tenant.id}
            mode="onboarding"
            onChanged={reloadQuests}
            onPendingChange={setSamplePending}
          />
        ) : null}
      />
    </Suspense>
  );
}
